// Chronological trip list, sortable by any column (default: discrepancy count
// desc, so the noisiest trips surface first).

import { useMemo, useState } from "react";
import type { TripSummary } from "../types.js";

type SortKey = "start" | "distanceM" | "discrepancyCount";

interface Props {
  trips: TripSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function TripList({ trips, selectedId, onSelect }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>("discrepancyCount");
  const [dir, setDir] = useState<"asc" | "desc">("desc");

  const sorted = useMemo(() => {
    const copy = [...trips];
    copy.sort((a, b) => {
      let cmp: number;
      if (sortKey === "start") cmp = a.start.localeCompare(b.start);
      else cmp = a[sortKey] - b[sortKey];
      return dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [trips, sortKey, dir]);

  function toggle(key: SortKey) {
    if (key === sortKey) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setDir(key === "start" ? "desc" : "desc");
    }
  }

  const arrow = (key: SortKey) => (key === sortKey ? (dir === "asc" ? " ↑" : " ↓") : "");

  if (trips.length === 0) return <p className="muted">No trips ingested yet.</p>;

  return (
    <table>
      <thead>
        <tr>
          <th onClick={() => toggle("start")}>Started{arrow("start")}</th>
          <th>Trip</th>
          <th onClick={() => toggle("distanceM")}>Distance{arrow("distanceM")}</th>
          <th onClick={() => toggle("discrepancyCount")}>Discrepancies{arrow("discrepancyCount")}</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((t) => (
          <tr
            key={t.id}
            className={t.id === selectedId ? "selected" : ""}
            onClick={() => onSelect(t.id)}
            style={{ cursor: "pointer" }}
          >
            <td>{new Date(t.start).toLocaleString()}</td>
            <td>{t.id}</td>
            <td>{(t.distanceM / 1000).toFixed(1)} km</td>
            <td>{t.discrepancyCount}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
